import i18n from 'i18next';
import { STREAMING_ASPECT_RATIOS } from 'utils/helpers/moba-games-statistics-options';
import { isMobile } from 'utils/is-mobile';
import { GameInfoAdditionalDataMap } from 'interfaces/sportsbook-data-levels';

export const LEAGUE_OF_LEGENDS_SETTINGS = {
  titles: {
    get 1() {
      return i18n.t('sportsbook.esportsKill');
    },
    get 2() {
      return i18n.t('sportsbook.baron');
    },
    get 3() {
      return i18n.t('sportsbook.dragon');
    },
    get 4() {
      return i18n.t('sportsbook.tower');
    },
    get 5() {
      return i18n.t('sportsbook.inhibitor');
    }
  },
  icons: {
    names: {
      1: 'kill',
      2: 'baron',
      3: 'dragon',
      4: 'tower',
      5: 'inhibitor'
    },
    firstSanctuary: {
      0: 'unknown-drake',
      1: 'chemtech-drake',
      2: 'cloud-drake',
      3: 'hexteck-drake',
      4: 'infernal-drake',
      5: 'mountain-drake',
      6: 'ocean-drake',
      7: 'elder-drake'
    },
    secondSanctuary: {
      1: 'rift-herald',
      2: 'baron-nashor-kill',
      3: 'voidgrub-kill'
    },
    positions: {
      dragons: isMobile()
        ? { top: '67%', left: '61%' }
        : { top: '65.5%', left: '61.2%' },
      epicMonsters: isMobile()
        ? { top: '23%', left: '34.8%' }
        : { top: '29%', left: '34.9%' },
      tower: isMobile()
        ? {
            1: { top: '82.1%', left: '18.5%' },
            2: { top: '86%', left: '20.9%' },
            3: { top: '66.1%', left: '15.5%' },
            4: { top: '71%', left: '28.9%' },
            5: { top: '90%', left: '31.9%' },
            6: { top: '49%', left: '17.5%' },
            7: { top: '62.8%', left: '36.5%' },
            8: { top: '88.8%', left: '46.5%' },
            9: { top: '23%', left: '15.5%' },
            10: { top: '52%', left: '39.9%' },
            11: { top: '92.8%', left: '65.5%' },
            12: { top: '0.1%', left: '32.1%' },
            13: { top: '38%', left: '55.9%' },
            14: { top: '67%', left: '82.9%' },
            15: { top: '4.1%', left: '50.4%' },
            16: { top: '26%', left: '59.9%' },
            17: { top: '40%', left: '78.9%' },
            18: { top: '2%', left: '63%' },
            19: { top: '19%', left: '66.9%' },
            20: { top: '23.5%', left: '79.5%' },
            21: { top: '6%', left: '74%' },
            22: { top: '9.5%', left: '76.5%' }
          }
        : {
            1: { top: '76.1%', left: '16.9%' },
            2: { top: '80%', left: '19.1%' },
            3: { top: '64.2%', left: '13.5%' },
            4: { top: '68.2%', left: '27.5%' },
            5: { top: '83.4%', left: '30.7%' },
            6: { top: '49.5%', left: '15.7%' },
            7: { top: '61.2%', left: '35.2%' },
            8: { top: '83.2%', left: '45.5%' },
            9: { top: '28.1%', left: '13.3%' },
            10: { top: '52.2%', left: '39.5%' },
            11: { top: '86.2%', left: '65.7%' },
            12: { top: '9.2%', left: '31.1%' },
            13: { top: '40.2%', left: '55.7%' },
            14: { top: '64.2%', left: '83.7%' },
            15: { top: '12.2%', left: '50%' },
            16: { top: '31.2%', left: '60%' },
            17: { top: '42.2%', left: '79.4%' },
            18: { top: '10.2%', left: '63.1%' },
            19: { top: '24.4%', left: '67%' },
            20: { top: '27.9%', left: '80%' },
            21: { top: '13.9%', left: '74%' },
            22: { top: '16.9%', left: '76.5%' }
          },
      kills: isMobile()
        ? {
            1: { top: '72.1%', left: '15.5%' },
            2: { top: '75.1%', left: '26.5%' },
            3: { top: '90.1%', left: '27.9%' },
            4: { top: '3%', left: '66.9%' },
            5: { top: '16%', left: '69%' },
            6: { top: '19%', left: '79%' }
          }
        : {
            1: { top: '69.1%', left: '13.5%' },
            2: { top: '71.1%', left: '24.8%' },
            3: { top: '84.1%', left: '25.9%' },
            4: { top: '11%', left: '66.9%' },
            5: { top: '22%', left: '69%' },
            6: { top: '24%', left: '80%' }
          }
    },
    getIconPositionsAndSizes: function (width: number) {
      const size = {
        width: width / STREAMING_ASPECT_RATIOS.desktop,
        height: width / STREAMING_ASPECT_RATIOS.desktop
      };

      return {
        0: {
          position: this.positions.dragons,
          size
        },
        1: {
          position: this.positions.epicMonsters,
          size
        },
        2: {
          size: isMobile()
            ? {
                width: width / STREAMING_ASPECT_RATIOS.mobile,
                height: width / STREAMING_ASPECT_RATIOS.mobile
              }
            : size,
          position: this.positions || {}
        }
      };
    }
  }
};

export const getLeagueOfLegendsStatistics = (
  additionalDataMap: GameInfoAdditionalDataMap | undefined
) => {
  return {
    statistics: {
      1: {
        1: additionalDataMap?.killScore?.team1 || 0,
        2: additionalDataMap?.baronScore?.team1 || 0,
        3: additionalDataMap?.elementarDragonScore?.team1 || 0,
        4: additionalDataMap?.towerScore?.team1 || 0,
        5: additionalDataMap?.inhibitor?.team1 || 0
      },
      2: {
        1: additionalDataMap?.killScore?.team2 || 0,
        2: additionalDataMap?.baronScore?.team2 || 0,
        3: additionalDataMap?.elementarDragonScore?.team2 || 0,
        4: additionalDataMap?.towerScore?.team2 || 0,
        5: additionalDataMap?.inhibitor?.team2 || 0
      }
    },
    mapState: {
      1: additionalDataMap?.mapState?.destroyedInhibitors || [],
      2: additionalDataMap?.mapState?.destroyedTowers || [],
      3: additionalDataMap?.mapState?.dragonType || null,
      4: additionalDataMap?.mapState?.epicMonsterType || -1
    }
  };
};
